import React, { useEffect, useRef, useState } from 'react';
import { 
  FaSitemap, FaHome, FaInfoCircle, FaPhone, FaImage,
  FaNewspaper, FaGraduationCap, FaBookOpen, FaUsers,
  FaChalkboardTeacher, FaFutbol, FaLaptop,
  FaShieldAlt, FaFileContract, FaArrowRight, FaEnvelope,
  FaPhoneAlt, FaMapMarkerAlt, FaExternalLinkAlt, FaUserTie,
  FaBuilding, FaUniversity, FaCalendarAlt, FaDownload,
  FaRegFileAlt, FaQuestionCircle, FaHeartbeat, FaSchool,
  FaDatabase, FaServer,FaSearch
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { MdOutlinePolicy, MdDescription, MdOutlineMap, MdLibraryBooks } from 'react-icons/md';

const Sitemap = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const sitemapSections = [
    {
      title: "Main Pages",
      icon: <FaHome />,
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      links: [
        { name: "Home", url: "/", icon: <FaHome />, description: "College homepage" },
        { name: "About Us", url: "/about", icon: <FaInfoCircle />, description: "About the college" },
        { name: "Contact Us", url: "/contact", icon: <FaPhone />, description: "Contact information" },
        { name: "Gallery", url: "/gallery", icon: <FaImage />, description: "Photo gallery" },
        { name: "News & Events", url: "/news", icon: <FaNewspaper />, description: "Latest updates" }
      ]
    },
    {
      title: "Academic",
      icon: <FaGraduationCap />,
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      links: [
        { name: "Courses", url: "/courses", icon: <FaBookOpen />, description: "All courses" },
        { name: "Course Details", url: "/course-details", icon: <FaGraduationCap />, description: "Detailed course info" },
        { name: "Admission Process", url: "/admisionprocess", icon: <FaUniversity />, description: "How to apply" },
        { name: "Fee Structure", url: "/fee-structure", icon: <FaFileContract />, description: "Fee details" },
        { name: "Registration", url: "/registration", icon: <FaRegFileAlt />, description: "Student registration" },
        { name: "Prospectus", url: "/prospectus", icon: <FaDownload />, description: "Download prospectus" }
      ]
    },
    {
      title: "Facilities",
      icon: <FaBuilding />,
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      links: [
        { name: "Library", url: "/library", icon: <MdLibraryBooks />, description: "Library resources" },
        { name: "Classrooms", url: "/classroom", icon: <FaChalkboardTeacher />, description: "Classroom facilities" },
        { name: "Games & Sports", url: "/gamespots", icon: <FaFutbol />, description: "Sports facilities" },
        { name: "Computer Lab", url: "/computer-lab", icon: <FaLaptop />, description: "IT infrastructure" }
      ]
    },
    {
      title: "Information",
      icon: <FaUsers />,
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      links: [
        { name: "Teaching Staff", url: "/teaching-staff", icon: <FaChalkboardTeacher />, description: "Faculty members" },
        { name: "Non-Teaching Staff", url: "/non-teaching-staff", icon: <FaUserTie />, description: "Support staff" },
        { name: "Cells & Association", url: "/commitee", icon: <FaUsers />, description: "College committees" },
        { name: "Principal's Message", url: "/principal", icon: <FaSchool />, description: "Message from Principal" },
        { name: "Director's Message", url: "/director", icon: <FaUserTie />, description: "Message from Director" }
      ]
    },
    {
      title: "Legal",
      icon: <FaShieldAlt />,
      color: "from-red-500 to-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      links: [
        { name: "Privacy Policy", url: "/privacy-policy", icon: <MdOutlinePolicy />, description: "Privacy information" },
        { name: "Terms & Conditions", url: "/terms-conditions", icon: <FaFileContract />, description: "Terms of use" },
        { name: "Refund Policy", url: "/free-refund", icon: <FaFileContract />, description: "Fee refund policy" },
        { name: "Fee Structure", url: "/fee-structure", icon: <FaFileContract />, description: "Fee details" }
      ]
    },
    {
      title: "Quick Links",
      icon: <FaExternalLinkAlt />,
      color: "from-pink-500 to-pink-600",
      bgColor: "bg-pink-50",
      borderColor: "border-pink-200",
      links: [
        { name: "Enquiry", url: "/enquiry", icon: <FaQuestionCircle />, description: "Submit enquiry" },
        { name: "Student Form", url: "/student-form", icon: <FaRegFileAlt />, description: "Student registration" },
        { name: "Download Prospectus", url: "/prospectus", icon: <FaDownload />, description: "College prospectus" },
        { name: "Introduction", url: "/introduction", icon: <FaInfoCircle />, description: "College introduction" },
        { name: "Mission & Vision", url: "/mision-vision", icon: <FaHeartbeat />, description: "Our mission and vision" }
      ]
    }
  ];

  const allLinks = sitemapSections.flatMap(section => 
    section.links.map(link => ({ ...link, category: section.title }))
  );

  const filteredLinks = allLinks.filter(link =>
    link.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    link.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    link.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = allLinks.length;

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-blue-50 via-white to-blue-100 py-16 min-h-screen">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-blue-500 rounded-full"></div>
            <div className="w-8 h-1 bg-blue-600 rounded-full"></div>
            <div className="w-6 h-1 bg-blue-700 rounded-full"></div>
          </div>

          {/* Breadcrumb */}
          <div className={`flex items-center justify-center gap-2 text-xs mb-6 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
          }`}>
            <span className="text-gray-500">Home</span>
            <span className="text-gray-400">/</span>
            <span className="text-blue-600 font-semibold">Sitemap</span>
          </div>

          {/* Heading */}
          <div className={`text-center mb-10 transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="flex justify-center mb-3">
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-3 rounded-2xl shadow-lg">
                <FaSitemap className="text-white text-3xl" />
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="text-blue-800">Sitemap</span>
            </h2>
            <div className="w-24 h-1 bg-blue-500 rounded-full mx-auto"></div>
            <p className="text-gray-600 text-sm mt-4">
              Complete website navigation structure - {totalPages} pages
            </p>
          </div>

          {/* Search Bar */}
          <div className={`mb-8 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="relative max-w-md mx-auto">
              <input
                type="text"
                placeholder="Search pages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 bg-white"
              />
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            </div>
          </div>

          {/* Sitemap Grid */}
          <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {sitemapSections.map((section, idx) => (
              <div key={idx} className={`${section.bgColor} rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1`}>
                <div className={`bg-gradient-to-r ${section.color} p-3 text-white`}>
                  <div className="flex items-center gap-2">
                    <div className="text-lg">{section.icon}</div>
                    <h3 className="font-bold text-md">{section.title}</h3>
                    <span className="ml-auto text-xs bg-white/20 px-2 py-0.5 rounded-full">
                      {section.links.length}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <ul className="space-y-2">
                    {section.links.map((link, linkIdx) => (
                      <li key={linkIdx}>
                        <Link 
                          to={link.url}
                          className="flex items-center gap-2 text-gray-600 text-sm hover:text-blue-600 transition-colors group"
                        >
                          <span className="text-gray-400 text-xs group-hover:text-blue-500">{link.icon}</span>
                          <span className="flex-1">{link.name}</span>
                          <FaArrowRight className="text-xs opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                        <p className="text-gray-400 text-xs pl-6">{link.description}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Search Results */}
          {searchTerm && (
            <div className={`mt-8 bg-white rounded-2xl shadow-md p-5 transition-all duration-500 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              <h3 className="font-bold text-gray-800 mb-3">Search Results ({filteredLinks.length})</h3>
              {filteredLinks.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-3">
                  {filteredLinks.map((link, idx) => (
                    <Link key={idx} to={link.url} className="flex items-center gap-2 p-2 hover:bg-blue-50 rounded-lg transition-all">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                        {link.icon}
                      </div>
                      <div>
                        <p className="text-gray-800 font-medium text-sm">{link.name}</p>
                        <p className="text-gray-400 text-xs">{link.category} • {link.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No pages found matching your search.</p>
              )}
            </div>
          )}

          {/* Statistics Section */}
          <div className={`mt-8 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-5 text-white transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold">{sitemapSections.length}</div>
                <div className="text-xs text-blue-200">Categories</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{totalPages}</div>
                <div className="text-xs text-blue-200">Total Pages</div>
              </div>
              <div>
                <div className="text-2xl font-bold">24/7</div>
                <div className="text-xs text-blue-200">Accessibility</div>
              </div>
              <div>
                <div className="text-2xl font-bold">100%</div>
                <div className="text-xs text-blue-200">Coverage</div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className={`mt-8 bg-white rounded-2xl shadow-md p-5 text-center transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <h3 className="font-bold text-gray-800 mb-2">Need Help Finding Something?</h3>
            <p className="text-gray-500 text-sm mb-3">Contact us for assistance navigating our website</p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <FaPhoneAlt className="text-blue-500" />
                <span>+91 7080 100 333</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <FaEnvelope className="text-blue-500" />
                <span>info@bnsmahila.edu</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <FaMapMarkerAlt className="text-blue-500" />
                <span>Akhari Bai-Pass, Varanasi</span>
              </div>
            </div>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-blue-700 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default Sitemap;